import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard } from "lucide-react";

export default function CheckoutPage() {
    const { items, subtotal } = useCart();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        email: "", first_name: "", last_name: "", phone: "",
        address_line1: "", address_line2: "", city: "", province: "QC", postal_code: "",
    });
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(false);
    const [provider, setProvider] = useState("stripe");

    useEffect(() => {
        if (items.length === 0) navigate("/panier");
    }, [items, navigate]);

    const quotePayload = useMemo(() => ({
        items: items.map((it) => ({ product_id: it.product_id, variant_id: it.variant_id, quantity: it.quantity, finition: it.finition || "" })),
        email: form.email || "guest@example.com",
        first_name: form.first_name || "-", last_name: form.last_name || "-",
        address_line1: form.address_line1 || "-", city: form.city || "-",
        province: form.province, postal_code: form.postal_code || "H0H0H0",
        origin_url: window.location.origin, payment_provider: provider,
    }), [items, form, provider]);

    useEffect(() => {
        if (items.length === 0) return;
        api.post("/checkout/quote", quotePayload)
            .then((r) => setQuote(r.data))
            .catch(() => setQuote(null));
    }, [quotePayload, items.length]);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = { ...quotePayload, ...form };
            if (provider === "stripe") {
                const r = await api.post("/checkout/stripe", payload);
                window.location.href = r.data.checkout_url;
            } else {
                const r = await api.post("/checkout/paypal", payload);
                window.location.href = r.data.approve_url;
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur de paiement");
            setLoading(false);
        }
    };

    const totals = quote || { subtotal, shipping: subtotal >= 500 ? 0 : 15, tps: 0, tvq: 0, total: subtotal + (subtotal >= 500 ? 0 : 15) };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="checkout-page">
            <h1 className="font-serif-luxe text-3xl sm:text-5xl chrome-text font-semibold mb-10">Finaliser la commande</h1>

            <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7 space-y-8">
                    <div className="glass-card rounded-2xl p-6 space-y-4">
                        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Contact</div>
                        <div>
                            <label>Courriel</label>
                            <input data-testid="checkout-email" type="email" required value={form.email} onChange={update("email")} />
                        </div>
                        <div>
                            <label>Téléphone (optionnel)</label>
                            <input data-testid="checkout-phone" type="text" value={form.phone} onChange={update("phone")} />
                        </div>
                    </div>

                    <div className="glass-card rounded-2xl p-6 space-y-4">
                        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Livraison</div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label>Prénom</label>
                                <input data-testid="checkout-first-name" required value={form.first_name} onChange={update("first_name")} />
                            </div>
                            <div>
                                <label>Nom</label>
                                <input data-testid="checkout-last-name" required value={form.last_name} onChange={update("last_name")} />
                            </div>
                        </div>
                        <div>
                            <label>Adresse</label>
                            <input data-testid="checkout-address1" required value={form.address_line1} onChange={update("address_line1")} />
                        </div>
                        <div>
                            <label>App. / bureau (optionnel)</label>
                            <input data-testid="checkout-address2" value={form.address_line2} onChange={update("address_line2")} />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-2">
                                <label>Ville</label>
                                <input data-testid="checkout-city" required value={form.city} onChange={update("city")} />
                            </div>
                            <div>
                                <label>Code postal</label>
                                <input data-testid="checkout-postal" required value={form.postal_code} onChange={update("postal_code")} />
                            </div>
                        </div>
                        <div>
                            <label>Province</label>
                            <select data-testid="checkout-province" value={form.province} onChange={update("province")}>
                                <option value="QC">Québec</option>
                            </select>
                        </div>
                    </div>

                    <div className="glass-card rounded-2xl p-6">
                        <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">Paiement</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                type="button"
                                data-testid="pay-provider-stripe"
                                onClick={() => setProvider("stripe")}
                                className={`rounded-xl p-4 border text-left transition-colors ${provider === "stripe" ? "border-white/60 bg-white/5" : "border-white/10"}`}
                            >
                                <CreditCard className="w-5 h-5 mb-2 text-slate-200" />
                                <div className="text-sm font-medium text-slate-100">Carte / Apple Pay / Google Pay</div>
                                <div className="text-xs text-slate-500 mt-1">Sécurisé par Stripe — ou en versements avec Klarna / Affirm</div>
                            </button>
                            <button
                                type="button"
                                data-testid="pay-provider-paypal"
                                onClick={() => setProvider("paypal")}
                                className={`rounded-xl p-4 border text-left transition-colors ${provider === "paypal" ? "border-white/60 bg-white/5" : "border-white/10"}`}
                            >
                                <div className="text-lg font-bold italic text-[#E2E8F0] mb-1">PayPal</div>
                                <div className="text-xs text-slate-500">Compte PayPal ou carte via PayPal</div>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-5">
                    <div className="glass-card rounded-2xl p-6 sticky top-24">
                        <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">Résumé</div>
                        <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                            {items.map((it) => (
                                <div key={`${it.product_id}-${it.variant_id}`} className="flex gap-3 items-center">
                                    <img src={fileUrl(it.image)} alt="" className="w-14 h-14 object-cover rounded-lg bg-black/40" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-slate-100 truncate">{it.name}</div>
                                        <div className="text-xs text-slate-500">{it.variant_label} × {it.quantity}</div>
                                    </div>
                                    <div className="text-sm text-slate-200">{(it.price_cad * it.quantity).toFixed(2)} $</div>
                                </div>
                            ))}
                        </div>
                        <div className="hair-divider my-4" />
                        <div className="space-y-1.5 text-sm">
                            <div className="flex justify-between"><span className="text-slate-400">Sous-total</span><span data-testid="checkout-subtotal">{totals.subtotal?.toFixed(2)} $</span></div>
                            <div className="flex justify-between"><span className="text-slate-400">Livraison</span><span data-testid="checkout-shipping">{totals.shipping === 0 ? "Offerte" : `${totals.shipping?.toFixed(2)} $`}</span></div>
                            <div className="flex justify-between"><span className="text-slate-400">TPS (5%)</span><span data-testid="checkout-tps">{totals.tps?.toFixed(2)} $</span></div>
                            <div className="flex justify-between"><span className="text-slate-400">TVQ (9.975%)</span><span data-testid="checkout-tvq">{totals.tvq?.toFixed(2)} $</span></div>
                        </div>
                        <div className="hair-divider my-4" />
                        <div className="flex justify-between items-baseline">
                            <span className="text-slate-200 font-medium">Total</span>
                            <span data-testid="checkout-total" className="font-serif-luxe text-3xl text-slate-100">{totals.total?.toFixed(2)} $ <span className="text-sm text-slate-500">CAD</span></span>
                        </div>
                        <button data-testid="checkout-submit-btn" type="submit" disabled={loading} className="btn-primary w-full mt-6">
                            {loading ? "Redirection…" : provider === "stripe" ? "Payer par carte" : "Payer avec PayPal"}
                        </button>
                        <div className="mt-4 text-[11px] text-slate-500 text-center">
                            Transaction chiffrée · Aucune donnée bancaire conservée
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
